import { type FC } from 'react'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import {
  LineChart as RechartLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { formatAxisLabel, getTickInterval, getValueFormatter, shouldRotateLabels, type ValueFormat } from '@egw/utils'
import { type DefaultProps } from '../common'

const COLORS = [
  '#7810F8',
  '#0088FF',
  '#FB21FF',
  '#0700DF',
  '#F26B2B',
  '#DC0385',
  '#46A800',
  '#05BDF4',
  '#BA27F3',
  '#FF009D',
  '#EA400D',
  '#20D5D5',
  '#ACD711',
  '#FCC505',
  '#1DE3A1',
]

export type LineChartData = Record<string, string | number>

export interface LineChartProps extends DefaultProps {
  /**
   * Data for the line chart
   */
  data: LineChartData[]
  /**
   * Chart title
   */
  title?: string
  /**
   * Description
   */
  description?: string
  /**
   * Title className
   */
  titleClassName?: string
  /**
   * Value format
   */
  valueFormat?: ValueFormat
  /**
   * Y axis label
   */
  yAxisLabel?: string
  /**
   * X axis label
   */
  xAxisLabel?: string
  /**
   * Show legend
   */
  isShowLegend?: boolean
}

export const LineChart: FC<LineChartProps> = ({
  data,
  title,
  description,
  className,
  id,
  testId,
  ariaLabel,
  titleClassName,
  valueFormat = 'number',
  yAxisLabel,
  xAxisLabel,
  isShowLegend = false,
}) => {
  // Guard clause: return null if data is empty
  if (data.length === 0) {
    return null
  }

  // Component base class names
  const baseClass = cx('flex flex-col items-center')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  const xAxisKey = Object.keys(data[0])[0]
  const lineRotate = shouldRotateLabels(data.length)
  const lineInterval = getTickInterval(data.length)
  const lineFormatter = getValueFormatter(valueFormat)

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
    >
      <div className={cx('flex outline-none flex-col items-center w-full h-full')}>
        <p className={cx('typo-primary-2xl-light text-text-header', titleClassName)}>{title}</p>
        {description ? <p className={cx('typo-primary-base-regular text-text-body mt-3xs')}>{description}</p> : null}
        <div
          className={cx('relative w-full h-full flex justify-center items-center', {
            'mt-lg': description ?? title,
          })}
        >
          <ResponsiveContainer
            width="100%"
            minHeight={326}
            minWidth={326}
          >
            <RechartLineChart
              data={data}
              margin={{
                top: 20,
                right: 40,
                left: yAxisLabel ? 60 : 20,
                bottom: lineRotate ? 80 : xAxisLabel ? 50 : 20,
              }}
            >
              <CartesianGrid stroke="#D8D9D8" strokeWidth={1} strokeDasharray="2 2" />
              <XAxis
                dataKey={xAxisKey}
                tick={{ fontSize: 12, fontWeight: 400, fontFamily: 'Open Sans', color: '#575B58' }}
                axisLine={{ stroke: '#D8D9D8' }}
                tickLine={false}
                angle={lineRotate ? -45 : 0}
                textAnchor={lineRotate ? 'end' : 'middle'}
                height={lineRotate ? 100 : 60}
                interval={lineInterval}
                tickFormatter={(value) => formatAxisLabel(value, data.length)}
              />
              <YAxis
                tick={{ fontSize: 12, fontWeight: 400, fontFamily: 'Open Sans', color: '#575B58' }}
                axisLine={{ stroke: '#D8D9D8' }}
                tickLine={false}
                tickFormatter={lineFormatter}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #EEEFEE',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                  fontSize: '14px',
                  fontFamily: 'Open Sans',
                  padding: '8px',
                }}
                formatter={lineFormatter}
              />
              {isShowLegend && <Legend iconType="line" />}
              {Object.keys(data[0])
                .slice(1)
                .map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={1}
                    dot={false}
                  />
                ))}
            </RechartLineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

